import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useRef, useState } from "react";
import {
  Parsed1120Data,
  Parsed1120FieldType,
  ParsedShareholder,
  useUpdateDraftValueMutation,
} from "store/apis/filingDraftReview";
import {
  resetFilingDraftForReview,
  setFilingDraftDeletedFields,
  setFilingDraftEditedFields,
  setFilingDraftErrorRemovedFields,
  setFilingDraftForReview,
} from "store/slices/filingDraftReview";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { DiscardFilingDraft } from "./DiscardFilingDraft";
import { Parse1120Loader } from "./Parse1120Loader";
import { Parsed1120Field } from "./Parsed1120Field";
import { SendDraftToCustomerConfirmation } from "./SendDraftToCustomerConfirmation";

export type ReviewParsed1120FieldsType = Parsed1120FieldType & {
  isEdited?: boolean;
};

export const ReviewParsed1120Fields = ({
  isOpen,
  close,
  isLoading,
  isSuccess,
  parsedData: createdParsedData,
}: ModalProps & {
  isLoading: boolean;
  isSuccess: boolean;
  parsedData?: Parsed1120Data;
}) => {
  const discardDraft = useModal();
  const sendConfirmation = useModal();
  const dispatch = useAppDispatch();
  const firstErrorFieldRef = useRef<HTMLDivElement | null>(null);
  const [updateField, { data: updatedParsedData }] =
    useUpdateDraftValueMutation();
  const { alertToast, successToast } = useToast();
  const {
    editedFields: defaultEditedFields,
    deletedFields: defaultDeletedFields,
    errorRemovedFields: defaultErrorRemovedFields,
  } = useAppSelector((store) => store.filingDraftReview);
  const [editedFields, setEditedFields] = useState<string[]>(
    defaultEditedFields || []
  );
  const [deletedFields, setDeletedFields] = useState<string[]>(
    defaultDeletedFields || []
  );
  const [errorRemovedFields, setErrorRemovedFields] = useState<string[]>(
    defaultErrorRemovedFields || []
  );
  const [editFieldKey, setEditFieldKey] = useState("");
  const disableOtherActions = Boolean(editFieldKey);
  const parsedData = updatedParsedData || createdParsedData;

  const fileUrl = createdParsedData?.file?.preview_url || "";

  const firstErrorIndex = parsedData?.fields?.findIndex(
    (field) =>
      !deletedFields.includes(field.key) &&
      !editedFields.includes(field.key) &&
      !errorRemovedFields.includes(field.key) &&
      (field.error_message || field.warning_message)
  );

  const handleFieldValueUpdate = async (
    values: Record<
      string,
      string | number | null | Array<Record<string, string>>
    >,
    fieldLabel: string
  ) => {
    try {
      await updateField({
        documentType: "FORM_1120",
        data: values,
      }).unwrap();
      successToast({ message: fieldLabel + " updated" });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    } finally {
      setEditFieldKey("");
    }
  };

  const handleEdit = (
    key: string,
    newValue: string | number,
    fieldLabel: string,
    oldValue: string | number
  ) => {
    if (newValue != oldValue && parsedData?.all_fields) {
      const updatedValue = newValue.toString().trim();
      const updatedObj = {
        ...parsedData?.all_fields,
        [key]:
          !updatedValue || isNaN(Number(updatedValue))
            ? updatedValue || null
            : Number(updatedValue),
      };
      setEditedFields((prev) => [...prev, key]);
      handleFieldValueUpdate(updatedObj, fieldLabel);
    } else {
      setEditFieldKey("");
    }
  };

  const handleArrayEdit = (
    key: string,
    newValue: ParsedShareholder[],
    fieldLabel: string,
    oldValue: ParsedShareholder[]
  ) => {
    if (
      JSON.stringify(newValue) !== JSON.stringify(oldValue) &&
      parsedData?.all_fields
    ) {
      const arr = Array.isArray(newValue) ? newValue : [];
      const updatedObj = { ...parsedData?.all_fields, [key]: arr };
      setEditedFields((prev) => [...prev, key]);
      handleFieldValueUpdate(updatedObj, fieldLabel);
    } else {
      setEditFieldKey("");
    }
  };

  const handleDelete = (key: string, fieldLabel: string) => {
    setDeletedFields((prev) => [...prev, key]);
    successToast({ message: fieldLabel + " deleted" });
  };

  const removeWarning = (fieldKey: string) => {
    setErrorRemovedFields((prev) => [...prev, fieldKey]);
  };

  const handleSendDraftToCustomer = () => {
    if (firstErrorFieldRef.current) {
      firstErrorFieldRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (parsedData) {
      dispatch(
        setFilingDraftForReview({
          file: createdParsedData?.file || null,
          fields: parsedData.fields.filter(
            (field) => !deletedFields.includes(field.key)
          ),
          all_fields: parsedData.all_fields,
        })
      );
      dispatch(setFilingDraftDeletedFields(deletedFields));
      dispatch(setFilingDraftEditedFields(editedFields));
      dispatch(setFilingDraftErrorRemovedFields(errorRemovedFields));
      sendConfirmation.close();
      close();
    }
  };

  const handleDiscardDraftClose = () => {
    dispatch(resetFilingDraftForReview());
    discardDraft.close();
    close();
  };

  const fieldsToDisplay =
    parsedData?.fields && parsedData?.fields.length > 0
      ? parsedData?.fields
      : [];

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content size="xxl">
          <Modal.Header>
            <Modal.Title>Review Parsed Fields</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="!t-p-0">
            <Async.Root
              isLoading={isLoading}
              isEmpty={false}
              isSuccess={isSuccess}
              customLoader={<Parse1120Loader />}
            >
              <Async.Empty>
                <></>
              </Async.Empty>
              <Async.Success>
                <div className="t-flex t-gap-1">
                  <div className="t-w-96 t-h-[500px] t-overflow-auto t-border t-border-solid t-border-l-0 t-border-t-0 t-border-b-0 t-border-neutral-10">
                    <div className="t-text-body-sm t-text-text-30 t-border t-border-solid t-border-l-0 t-border-t-0 t-border-r-0 t-border-neutral-10 t-px-8 t-py-3">
                      Review the extracted fields and make sure they match the
                      document. You can correct any mismatches using the edit
                      button.
                    </div>
                    {fieldsToDisplay
                      .filter((field) => !deletedFields.includes(field.key))
                      .map((field, idx) => (
                        <div
                          key={field.key}
                          ref={
                            idx === firstErrorIndex ? firstErrorFieldRef : null
                          }
                        >
                          <Parsed1120Field
                            field={field}
                            isEdited={editedFields.includes(field.key)}
                            onDelete={() =>
                              handleDelete(field.key, field.label)
                            }
                            onEdit={(newValue) =>
                              handleEdit(
                                field.key,
                                newValue,
                                field.label,
                                field.value as string | number
                              )
                            }
                            onArrayEdit={(newValue) =>
                              handleArrayEdit(
                                field.key,
                                newValue,
                                field.label,
                                field.value as ParsedShareholder[]
                              )
                            }
                            isEditable
                            isEditing={field.key === editFieldKey}
                            setEditFieldKey={setEditFieldKey}
                            disableActions={
                              editFieldKey ? field.key !== editFieldKey : false
                            }
                            removeWarning={() => removeWarning(field.key)}
                            isRemoved={errorRemovedFields.includes(field.key)}
                          />
                        </div>
                      ))}
                  </div>
                  <iframe
                    className="t-flex-1 t-h-[500px]"
                    src={fileUrl}
                    title="Document Preview"
                  />
                </div>
              </Async.Success>
            </Async.Root>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={discardDraft.open} disabled={disableOtherActions}>
              Cancel
            </Button>
            <Button
              customType="primary"
              disabled={isLoading || disableOtherActions}
              onClick={handleSendDraftToCustomer}
            >
              Send to customer
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      <DiscardFilingDraft
        {...discardDraft}
        onDiscard={handleDiscardDraftClose}
      />
      <SendDraftToCustomerConfirmation
        {...sendConfirmation}
        onSend={handleSendDraftToCustomer}
      />
    </>
  );
};
